import { AccordionItem, AccordionItemContent } from '@e3dc-react/shell/Elements/Accordion'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, CircularProgress, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { TFunction } from 'i18next'
import React from 'react'
import { useWatch } from 'react-hook-form'
import useModals from 'src/Hooks/useModals'
import { createModbusTcpServer, updateModbusTcpServer } from '../api/modbus-tcp-server'
import { modbusTcpServerKey, useInstallationForm, useModbusTcpServer } from 'src/Libs/flow/installation/hooks'
import { getDaysUntilIsoDate } from 'src/Libs/flow/installation/utils'
import useAccordionStyle from 'src/Libs/styles'
import { errorFormatter } from '../../../parsers/error-formatter'
import { SettingsBox } from 'src/Elements/Data'
import { syncInstallation } from 'src/Libs/flow/installation/api'
import { ModbusTCPServerDtoPOSTorPUTDto, ModbusTCPServerLicenseType } from '../types/modbus-tcp-server.types'
import { useStyles } from 'src/Components/toggle/Toggle'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import { ApiResult, usePortalMutation } from 'src/Libs/api'

export const ModbusTcpServer: React.FC = () => {
  const { t } = useCustomTranslation()
  const { classes } = useAccordionStyle()
  const authFetch = useAuthFetchWithResponse()

  const { openActivateModbusTcpServerDialog } = useModals()
  const { enqueueErrorStack } = useSnackbar()

  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()

  const { control } = useInstallationForm()
  const [installationId, emc] = useWatch({ control, name: ['id', 'emcDevice'] })

  const isNewInstallation = installationId === null
  const hasSavedEmc = !!emc?.deviceId
  const isEmcDisconnected = !emc?.deviceStatus || emc.deviceStatus === 'unknown'

  const { data, isPending } = useModbusTcpServer(installationId ?? undefined)

  const isNewModbusTcpServer = data === null

  const queryClient = useQueryClient()
  const { mutate, isPending: isMutationPending } = usePortalMutation({
    mutationFn: async ({ type, active }: { type: ModbusTCPServerLicenseType; active: boolean }) => {
      if (installationId === null || data === undefined) return new ApiResult<null>()

      const payload: ModbusTCPServerDtoPOSTorPUTDto =
        type === 'disabled'
          ? {
              active: false,
            }
          : {
              active,
              type,
            }

      if (isNewModbusTcpServer && type !== 'disabled') {
        return createModbusTcpServer(installationId, payload, authFetch)
      } else {
        return updateModbusTcpServer(installationId, payload, authFetch)
      }
    },
    onSuccess: () => {
      if (installationId === null) return

      syncInstallation(installationId, authFetch).finally(() => {
        queryClient.invalidateQueries({ queryKey: modbusTcpServerKey(installationId) })
      })
    },
    onError: error => {
      enqueueErrorStack(t('installation.modbusTCPServer.active.error', '', { error: errorFormatter(error) }))
    },
  })

  const isLoading = data === undefined || isPending || isMutationPending

  const handleChange = (checked: boolean): void => {
    if (isLoading) return

    if (checked) {
      openActivateModbusTcpServerDialog({
        latestData: data,
        active: isNewModbusTcpServer,
        onSave: type => mutate({ type, active: true }),
      })
    } else {
      mutate({ type: 'disabled', active: false })
    }
  }

  const isActive = !!data?.active
  const toolTipTitle = getModbusTcpSwitchTitle(isActive, hasSavedEmc, t)

  const showRemainingDaysMessage = isActive && (data?.type === 'free_trial' || data?.type === 'free_trial_continuation')

  const remainingDays: number = data?.expirationDate ? getDaysUntilIsoDate(data?.expirationDate) : 0

  const isDisabled = isLoading || !canEdit || isEmcDisconnected
  const { classes: toggleClasses } = useStyles({ disabled: isDisabled })

  const usedLabel = isNewInstallation
    ? t('installation.modbusTCPServer.modbus-server-active-info-deactivated')
    : isActive
    ? t('installation.modbusTCPServer.modbus-server-active-info-activated')
    : t('installation.modbusTCPServer.modbus-server-active-info-deactivated')

  return (
    <AccordionItem title={t('installation.modbusTCPServer.modbus-tcp-server')} expanded className={classes.accordion}>
      <AccordionItemContent variant="paper">
        <Stack>
          <SettingsBox
            title={usedLabel}
            suffix={
              <Tooltip title={toolTipTitle}>
                <FormControlLabel
                  control={
                    <Switch
                      className={toggleClasses.switchStyle}
                      checked={isActive}
                      onChange={(_e, checked) => handleChange(checked)}
                      disabled={isDisabled}
                    />
                  }
                  label={isLoading ? <CircularProgress size={24} /> : usedLabel}
                  disabled={isDisabled}
                />
              </Tooltip>
            }
          />
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            <Tooltip title={t('installation.modbusTCPServer.modbus-server-subscription-status')}>
              <Box component={'span'}>
                <Button variant="outlined" size="large" disabled>
                  {t('installation.modbusTCPServer.modbus-server-subscription-info')}
                </Button>
              </Box>
            </Tooltip>
          </Box>
        </Stack>

        {showRemainingDaysMessage && (
          <Typography visibility={showRemainingDaysMessage ? undefined : 'hidden'} sx={{ pt: showRemainingDaysMessage ? 1.5 : 0 }}>
            {t('installation.modbusTCPServer.modbus-tcp-sever-active', {
              days: remainingDays,
            })}
          </Typography>
        )}
      </AccordionItemContent>
    </AccordionItem>
  )
}

function getModbusTcpSwitchTitle(modbusTcpServerIsActive: boolean, hasSavedEmc: boolean, t: TFunction): string {
  // Server is already active. No need for a message.
  if (modbusTcpServerIsActive) return ''

  // Can not activate server if there is no EMC. Display message to warn the user.
  if (!hasSavedEmc) return t('installation.modbusTCPServer.modbus-tcp-sever-not-paired-info')

  return ''
}
